import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { lazy, Suspense, useContext, useEffect, useRef, useState, Fragment } from 'react'
import { ItemLine } from './ItemLine'
import { CanalDuVisage } from './Visage'
import { TirerPourRafraichir } from './TirerPourRafraichir'
import { useRafraichissementDuFil } from './CanalDuFil'
import { reagir, type Trace } from './humeur'
import { CarteDeDecouverte } from '@/features/decouverte/CarteDeDecouverte'
import { FeedSkeleton } from '@/components/ui/Skeleton'
import { useToasts } from '@/components/ui/Toasts'
import {
  enSoumission,
  envoyerReponses,
  marquer,
  nouvelIdentifiant,
  pageDuFil,
  signaler,
  type Carte,
  type Devoilement,
  type ModeDuFil,
  type MotifDeSignalement,
  type PageDuFil,
  type ReponseComposee,
} from '@/lib/api/feed'
import { refusEnFrancais } from '@/lib/api/enFrancais'
import { auRetourDesVerdicts } from '@/lib/hors-ligne/file'
import { sonFaux, sonJuste } from '@/lib/son'

/**
 * **La feuille de partage se charge à l'ouverture** — PR-2.
 *
 * Elle vit dans `AmisScreen`, et l'importer statiquement tirait TOUT l'écran
 * Mon Cercle — demandes, conversations, recherche par pseudonyme — dans le
 * paquet principal, alors que le Fil est justement celui qu'on veut le plus
 * léger possible. Or partager une question est un geste rare : le candidat
 * révise, il ne poste pas.
 *
 * Le chargement part à la tape sur l'icône, et la feuille monte du bas :
 * l'animation couvre l'aller-retour.
 */
const FeuilleDePartage = lazy(async () => ({
  default: (await import('@/features/amis/AmisScreen')).FeuilleDePartage,
}))

/**
 * **Le partage HORS du site, chargé lui aussi à la tape** — [DECISIONS §192].
 *
 * Même raison, et elle vaut double ici : c'est un geste qu'on fait au plus
 * trois fois par jour, et il vit dans le menu des trois points. Le mettre dans
 * le paquet principal ferait payer sa feuille à tous les candidats qui ne
 * l'ouvriront jamais.
 */
const FeuilleDePartageExterne = lazy(async () => ({
  default: (await import('./PartageExterne')).FeuilleDePartageExterne,
}))

/**
 * Le Fil — 02 §F2.2.
 *
 * **Infini** (RG-2.12) : aucun plafond d'items neufs, aucune limite de session,
 * aucun écran de fin. Le candidat s'arrête quand il décide de s'arrêter.
 *
 * **Le défilement ne doit jamais s'interrompre sur une attente réseau**
 * (RG-2.13) : la page suivante se demande dès la septième carte, pas quand le
 * candidat touche le bas. Trois cartes d'avance, c'est le temps d'un aller-
 * retour sur une 3G lente.
 */
export function Feed({
  economieDeDonnees = false,
  mode = 'feed',
  onQuiz,
  onOuvrirOnglet,
}: {
  economieDeDonnees?: boolean | undefined
  mode?: ModeDuFil | undefined
  /**
   * Où mène la carte de découverte — F22 §7, [DECISIONS §366].
   *
   * L'application n'a pas de routeur : elle tient un onglet en état dans
   * `App.tsx`. La carte ne peut donc pas naviguer seule, et cette fonction
   * descend depuis là. Absente, la carte ne s'affiche pas — une invitation
   * dont le bouton ne mène nulle part serait pire que pas d'invitation.
   */
  onOuvrirOnglet?: ((onglet: string) => void) | undefined
  /** RG-2.38 — une carte mène à son tag, pour lancer un quiz dessus. */
  /**
   * `item` accompagne le tag depuis §244 : c'est lui qui permet au serveur
   * de choisir LA section qui traite la question, et non la première du
   * chapitre. Facultatif — les archives ouvrent la même porte sans item.
   */
  onQuiz?:
    | ((tag: { path: string; label: string; item?: string }) => void)
    | undefined
}) {
  const [devoilements, setDevoilements] = useState<Record<string, Devoilement>>({})
  const [enVol, setEnVol] = useState<Record<string, true>>({})

  // **Les cartes dont la réponse dort en file** — RG-9.4, §111. `cle → uuid` et
  // non l'inverse : c'est par la clé qu'on retrouve la carte à l'écran, et par
  // l'uuid que le serveur rend le verdict au retour du réseau.
  const [enFile, setEnFile] = useState<Record<string, string>>({})

  // RG-2.40 — l'historique de la séance, dans l'ordre. C'est lui qui rend le
  // visage possible : sans mémoire des dernières réponses et de leurs thèmes,
  // il ne serait qu'une décoration, et une décoration qui félicite au hasard
  // perd la confiance qu'on a dans ses conseils.
  const [traces, setTraces] = useState<Trace[]>([])

  // La question qu'on s'apprête à envoyer à un ami, ou `null`. Une feuille
  // pour tout le fil et non une par carte : cent feuilles montées d'avance
  // coûteraient cent requêtes de liste d'amis.
  const [aPartager, setAPartager] = useState<string | null>(null)

  /**
   * La question dont on veut l'adresse publique, ou `null` — [DECISIONS §192].
   *
   * Elle porte sa VERSION et son énoncé : la version parce que c'est ce que le
   * serveur reçoit, l'énoncé parce que le message copié le contient — « un lien
   * seul dans une conversation ne se clique pas ». Une feuille pour tout le fil,
   * comme celle des amis, et pour la même raison.
   */
  const [aSortir, setASortir] = useState<{ version: number; enonce: string } | null>(null)
  const [marques, setMarques] = useState<Record<string, boolean>>({})
  const [signales, setSignales] = useState<Record<string, true>>({})

  /**
   * La graine de consultation — [DECISIONS §259].
   *
   * **Elle entre dans la clé de requête, et c'est ce qui fait tout.** Un
   * rafraîchissement produit une nouvelle graine, donc une nouvelle clé, donc
   * une liste entièrement neuve ; les pages d'une même consultation la
   * partagent et se suivent sans doublon.
   *
   * Ben : « il n'y a toujours pas assez de rotation ; quand on refresh on doit
   * trouver du contenu nouveau. » Le périmètre porte trois mille questions, la
   * réserve en tirait trois cents, et ces trois cents étaient les mêmes
   * pendant trois heures.
   */
  const [session, setSession] = useState(() => Math.random().toString(36).slice(2, 12))

  const fil = useInfiniteQuery({
    queryKey: ['fil', mode, session],
    queryFn: ({ pageParam }) => pageDuFil(pageParam, mode, session),
    initialPageParam: 0,
    getNextPageParam: (derniere: PageDuFil) => derniere.meta.next_page,
    // **Le Fil SURVIT au changement d'onglet.** Il était remis à zéro à chaque
    // retour — aller voir les Archives une seconde et revenir effaçait les
    // quinze cartes qu'on venait de descendre, avec leurs corrections. C'est
    // du travail perdu, et sur une connexion comptée c'est aussi de l'argent.
    //
    // Le tirage reste frais quand on le demande : c'est le geste de
    // rafraîchissement — tirer vers le bas depuis le sommet — qui recompose,
    // pas la navigation.
    staleTime: Infinity,
    gcTime: 30 * 60_000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  })

  const client = useQueryClient()
  const toasts = useToasts()

  const repondre = useMutation({
    mutationFn: ({
      carte,
      reponse,
      tempsMs,
      uuid,
    }: {
      carte: Carte
      reponse: ReponseComposee
      tempsMs: number
      // **Généré par l'APPELANT** — §111. Il en a besoin pour retrouver la
      // carte quand le verdict revient d'une file vidée bien plus tard ; le
      // fabriquer ici l'aurait rendu inconnaissable au-dehors.
      uuid: string
    }) =>
      envoyerReponses([
        {
          client_uuid: uuid,
          form_version_id: carte.form_version_id,
          // RG-04-20 — les options réellement présentées, dans leur ordre
          // d'affichage. Sans elles, ni statistiques par distracteur ni rejeu.
          shown_option_ids: carte.options.map((o) => o.id),
          // §150 — un appariement envoie `{paires: {…}}`, tout le reste une
          // liste nue : c'est la seule forme qui porte deux colonnes.
          submitted: enSoumission(reponse),
          answered_at: new Date().toISOString(),
          time_spent_ms: tempsMs,
        },
      ],
        // **La LISTE se déclare** — RG-2.41, [DECISIONS §101]. Le Fil envoyait
        // `feed` quel que soit le mode ouvert : l'objectif du jour s'atteignait
        // en relisant dix marque-pages ou dix questions déjà ratées. Le
        // compteur appliquait pourtant la bonne règle — il ne retient que le
        // Fil et le quiz chronométré — mais sur une donnée qui mentait.
        //
        // Les réponses continuent d'alimenter la maîtrise et la répétition
        // espacée : le journal est unique (D-15), c'est l'OBJECTIF qui se
        // restreint, pas la mémoire.
        mode === 'bookmarks' ? 'bookmarks' : mode === 'review' ? 'review' : 'feed',
      ),
    // **La pastille d'objectif ne bougeait JAMAIS** — [DECISIONS §82].
    // `FeedScreen` porte le commentaire « le Fil invalide cette clé après
    // chaque salve » ; le Fil ne l'invalidait pas. Avec un `staleTime` d'une
    // minute, aucun rafraîchissement au retour de fenêtre et aucune
    // invalidation, la pastille gardait la valeur du montage : on répondait
    // vingt fois, elle affichait toujours `0 / 100`.
    onSuccess: () => client.invalidateQueries({ queryKey: ['progression'] }),
  })

  const marquePage = useMutation({
    mutationFn: ({ itemId, actif }: { itemId: string; actif: boolean }) => marquer(itemId, actif),
  })

  const signalement = useMutation({
    mutationFn: ({ versionId, motif }: { versionId: number; motif: MotifDeSignalement }) =>
      signaler(versionId, motif),
  })

  // **Chaque OCCURRENCE est une carte à part** — RG-2.4ter. Le Fil est de la
  // répétition espacée : une question revient précisément parce qu'il faut la
  // REFAIRE, et deux occurrences indexées sur le même identifiant partageaient
  // leur dévoilement — la seconde arrivait déjà corrigée, explication ouverte.
  // Le candidat croyait l'avoir travaillée, et défilait.
  //
  // Le marque-page, lui, reste indexé par QUESTION : ce n'est pas un résultat
  // mais un rangement, et marquer l'une doit remplir les deux.
  const cartes = (fil.data?.pages.flatMap((page) => page.data) ?? []).map(
    (carte, rang) => ({ carte, cle: `${carte.item_id}#${String(rang)}` }),
  )
  const epuise = fil.data?.pages.at(-1)?.meta.exhausted ?? false

  // RG-2.40 — le visage vit dans l'en-tête (RG-D22 amendée) : le Fil calcule
  // la réaction, l'en-tête l'affiche. Passer par un contexte évite de traverser
  // `FeedScreen` et `App`, qui n'ont rien à en faire.
  const { poser } = useContext(CanalDuVisage)

  useEffect(() => {
    poser(reagir(traces, mode))
  }, [traces, mode, poser])

  // **Le verdict revient s'il peut** — §111. Quand la file se vide, le serveur
  // renvoie les dévoilements du lot, indexés par `client_uuid` : les cartes
  // encore à l'écran s'affichent alors corrigées. Celles qu'on a fait défiler,
  // non — et rien ne le promet.
  useEffect(
    () =>
      auRetourDesVerdicts((reveals) => {
        setEnFile((avant) => {
          const restants: Record<string, string> = {}
          const arrives: Record<string, Devoilement> = {}

          for (const [cle, uuid] of Object.entries(avant)) {
            const devoilement = reveals[uuid]

            if (devoilement === undefined) restants[cle] = uuid
            else arrives[cle] = devoilement
          }

          if (Object.keys(arrives).length > 0) {
            setDevoilements((deja) => ({ ...deja, ...arrives }))

            // ── LE SON, POUR CE CHEMIN AUSSI — §392 ──────────────────────
            //
            // **Un verdict arrive par DEUX chemins**, et n'en câbler qu'un
            // laisse la moitié des réponses muettes. Vu à l'écran : la première
            // carte sonnait, la deuxième non — son verdict passait par ici.
            //
            // **Un seul son par lot.** Le candidat a répondu à ces cartes comme
            // à un groupe ; en jouer cinq d'un coup ferait un accord que
            // personne n'a demandé. Juste si tout le lot l'est, faux dès qu'une
            // réponse ne l'est pas : c'est le résumé honnête du groupe.
            const verdicts = Object.values(arrives)

            if (verdicts.every((v) => v.is_correct)) {
              sonJuste()
            } else {
              sonFaux()
            }
          }

          return restants
        })
      }),
    [],
  )

  const atterri = (cle: string): void => {
    setEnVol((avant) => Object.fromEntries(Object.entries(avant).filter(([k]) => k !== cle)))
  }

  const envoyer = (carte: Carte, cle: string, reponse: ReponseComposee, tempsMs: number): void => {
    setEnVol((avant) => ({ ...avant, [cle]: true }))

    const uuid = nouvelIdentifiant()

    repondre.mutate(
      { carte, reponse, tempsMs, uuid },
      {
        onSuccess: (resultat) => {
          const [devoilement] = Object.values(resultat.reveals)

          // **Pas de verdict, mais la réponse est prise** — RG-9.4, §111. Hors
          // connexion, `envoyerReponses` met en file et rend un résultat sans
          // dévoilement : la carte se ferme en disant ce qui s'est passé, au
          // lieu d'afficher une correction qu'elle n'a pas.
          if (devoilement === undefined) {
            setEnFile((avant) => ({ ...avant, [cle]: uuid }))
          }

          if (devoilement !== undefined) {
            setDevoilements((avant) => ({ ...avant, [cle]: devoilement }))

            // §392 — ici, et pas au rendu : c'est le seul endroit où le verdict
            // ARRIVE. Le poser dans la carte le rejouerait à chaque rendu.
            if (devoilement.is_correct) {
              sonJuste()
            } else {
              sonFaux()
            }

            setTraces((avant) => [
              // Vingt réponses suffisent : les règles ne regardent jamais plus
              // loin, et garder toute la séance ferait grossir l'état sans
              // rien changer à ce qui s'affiche.
              ...avant.slice(-19),
              {
                juste: devoilement.is_correct,
                theme: carte.tag.path,
                libelle: carte.tag.label,
                // **Vrai par construction dans « À revoir ».** Cette liste ne
                // contient QUE des questions déjà ratées ; ailleurs, on ne le
                // sait pas, et l'affirmer serait inventer.
                dejaRatee: mode === 'review',
                // Ce que le visage doit savoir pour ne pas proposer de marquer
                // ce qui l'est déjà.
                marquee: (marques[carte.item_id] ?? carte.marquee ?? false) || mode === 'bookmarks',
              },
            ])
          }

          atterri(cle)
        },
        onError: () => {
          // Le réseau a lâché. On rend la main plutôt que de laisser la carte
          // muette : le candidat retapera, et l'identifiant du second essai
          // n'est pas le même — l'idempotence porte sur l'envoi, pas sur le
          // geste. Le journal hors ligne (L3) prendra le relais ici.
          atterri(cle)
        },
      },
    )
  }

  // RG-2.13ter — le SEUL geste qui recompose, quel que soit l'écran. Il vide
  // aussi ce que la séance avait accumulé : des corrections affichées sur des
  // cartes qui ne sont plus là seraient pire que rien.
  const rafraichir = (): void => {
    setDevoilements({})
    setSignales({})

    /*
     * **Une graine neuve, et non un `refetch()`** — [DECISIONS §259].
     *
     * Rejouer la même requête redonnait exactement les mêmes questions : la
     * réserve est tirée d'un hachage salé, et à sel constant elle est
     * constante. Changer la graine change la clé de requête, donc la liste
     * entière — c'est le « quand on refresh on doit trouver du contenu
     * nouveau » de Ben, et cela ne se règle pas côté cache.
     */
    setSession(Math.random().toString(36).slice(2, 12))
  }

  // **La commande vit dans l'EN-TÊTE** — §88. Elle s'affichait en pilule
  // pleine, centrée au-dessus de la première carte : la commande la plus rare
  // de l'écran occupait la place la plus visible, et poussait le contenu vers
  // le bas à chaque page. Le geste tactile, lui, ne change pas.
  //
  // **Déclaré AVANT les sorties anticipées.** Placé après, il n'était appelé
  // que sur la branche qui rend des cartes : un `Vide` puis un fil rempli
  // changeaient le nombre de crochets entre deux rendus.
  useRafraichissementDuFil(rafraichir, fil.isRefetching)

  if (fil.isLoading) return <FeedSkeleton />

  if (cartes.length === 0) return <Vide mode={mode} />

  // **Le contexte change le ton, pas les faits.** Se tromper dans « à revoir »
  // est normal — ce sont des questions déjà ratées — et le dire sur le ton du
  // Fil ferait d'une séance de rattrapage une séance d'échecs.

  return (
    <TirerPourRafraichir
      enCours={fil.isRefetching}
      // RG-2.13ter — le SEUL geste qui recompose. Il vide aussi ce que la
      // séance avait accumulé : des corrections affichées sur des cartes qui
      // ne sont plus là seraient pire que rien.
      onRafraichir={rafraichir}
    >
      {epuise ? (
        // RG-2.14 — le fil ne se termine jamais ; il le dit quand il ressert.
        <p className="px-4 py-2 text-[13px]" style={{ color: 'var(--texte-2)' }}>
          Vous avez fait le tour de vos thèmes — révision des plus anciens.
        </p>
      ) : null}

      {aPartager !== null && (
        // Sans repli visible : la feuille monte du bas, et un squelette de
        // trois lignes qui clignote avant elle serait plus voyant que le
        // court instant qu'il couvre.
        <Suspense fallback={null}>
          <FeuilleDePartage itemId={aPartager} onFerme={() => { setAPartager(null); }} />
        </Suspense>
      )}

      {aSortir !== null && (
        <Suspense fallback={null}>
          <FeuilleDePartageExterne
            formVersionId={aSortir.version}
            enonce={aSortir.enonce}
            onFerme={() => { setASortir(null); }}
          />
        </Suspense>
      )}

      {cartes.map(({ carte, cle }, rang) => (
        <Fragment key={cle}>
          {/*
            **La carte de découverte est POSÉE DANS LE FIL, entre deux
            questions** — F22 §7, [DECISIONS §366].

            Ni fenêtre modale, ni bannière collée en haut : une carte se fait
            défiler, et passer sans y toucher est une réponse que le serveur
            sait lire. Elle vient après la troisième question — assez loin pour
            que le candidat ait commencé à réviser, assez tôt pour qu'il la
            voie sans dérouler.

            Seulement dans le fil NATUREL : « À revoir » et les marque-pages
            sont des listes que le candidat a lui-même composées, et y glisser
            une invitation serait s'inviter dans son rangement.
          */}
          {rang === 3 && mode === 'feed' && onOuvrirOnglet !== undefined && (
            <CarteDeDecouverte onOuvrir={onOuvrirOnglet} />
          )}

        <Sentinelle
          key={cle}
          actif={rang === cartes.length - 3 && fil.hasNextPage && !fil.isFetchingNextPage}
          onAtteinte={() => {
            void fil.fetchNextPage()
          }}
        >
          <ItemLine
            carte={carte}
            devoilement={devoilements[cle]}
            enAttente={enVol[cle] === true}
            // **La réponse dort en file** — RG-9.4, §111. La carte est close —
            // elle a été répondue — mais sans verdict : il n'existe pas encore.
            // Le taire laisserait croire à un envoi silencieusement perdu, ce
            // qui est exactement ce qu'on vient de cesser de faire.
            enFile={enFile[cle] !== undefined}
            economieDeDonnees={economieDeDonnees}
            // **L'état vient du SERVEUR, et le local ne fait que le devancer.**
            // Partir d'un état vide faisait revenir creux, le lendemain, un
            // marque-page posé la veille — et le candidat le reposait, sans
            // effet puisqu'il y était déjà.
            marque={marques[carte.item_id] ?? carte.marquee ?? false}
            signale={signales[cle] === true}
            onRepondre={(reponse, tempsMs) => {
              envoyer(carte, cle, reponse, tempsMs)
            }}
            onMarquer={(actif) => {
              // L'état bascule TOUT DE SUITE : le candidat a tapé, il doit
              // voir que c'est fait. Si le réseau refuse, on revient en
              // arrière — c'est le seul cas où l'affichage ment une seconde,
              // et il ment dans le sens qui ne coûte rien.
              setMarques((avant) => ({ ...avant, [carte.item_id]: actif }))

              marquePage.mutate(
                { itemId: carte.item_id, actif },
                {
                  onError: () => {
                    setMarques((avant) => ({ ...avant, [carte.item_id]: !actif }))

                    // §338 — le signet redevient creux tout seul, et l'on croit
                    // avoir raté la cible. Le retrouver absent des marque-pages
                    // le lendemain se lit comme une perte de données.
                    toasts.refuser(
                      actif
                        ? 'Ce marque-page n’a pas été enregistré. Vérifiez votre connexion et réessayez.'
                        : 'Ce marque-page n’a pas pu être retiré. Vérifiez votre connexion et réessayez.',
                    )
                  },
                },
              )
            }}
            onSignaler={(motif) => {
              // **L'état bascule tout de suite, ET il revient si l'envoi
              // échoue** — RG-D15bis.
              //
              // Il ne revenait pas : la carte restait marquée « signalée »
              // alors que rien n'était parti. C'est le cas le plus grave du
              // relevé, parce que signaler est un geste qu'on ne refait pas —
              // le candidat a vu la confirmation, il descend, et le
              // signalement n'existera jamais. Une question fausse reste
              // servie à tout le monde parce qu'un seul envoi s'est perdu.
              setSignales((avant) => ({ ...avant, [cle]: true }))

              signalement.mutate(
                { versionId: carte.form_version_id, motif },
                {
                  onError: (erreur: unknown) => {
                    setSignales((avant) =>
                      Object.fromEntries(Object.entries(avant).filter(([k]) => k !== cle)),
                    )

                    /*
                     * **Et il faut le DIRE** — [DECISIONS §338].
                     *
                     * RG-D15bis a corrigé la moitié du défaut : la carte
                     * cessait de mentir. L'autre moitié restait — elle
                     * redevenait ordinaire sans un mot, sur un écran qu'on est
                     * en train de faire défiler. Signaler est le geste qu'on ne
                     * refait pas : le candidat a fait le sien, il continue, et
                     * une question fausse reste servie à tout le monde parce
                     * qu'un seul envoi s'est perdu en silence.
                     *
                     * Aucun toast au succès : la carte porte déjà sa mention.
                     *
                     * **Et il faut dire POURQUOI** — §406. Ce texte était écrit
                     * en dur : il accusait la connexion quel que soit le refus.
                     * Ben, le 8 septembre 2026 : « à chaque fois que je fais un
                     * signalement j'obtiens ce message alors que je suis
                     * connecté et tout fonctionne ». Le serveur disait en
                     * réalité que le quota du jour était épuisé, et l'écran le
                     * taisait — alors on refait le geste, on échoue encore, et
                     * on conclut que l'application est cassée.
                     */
                    toasts.refuser(
                      refusEnFrancais(
                        erreur,
                        'Le signalement n’est pas parti. Vérifiez votre connexion et refaites-le.',
                      ),
                    )
                  },
                },
              )
            }}
            onPartager={() => { setAPartager(carte.item_id); }}
            // RG-2.47, §192 — l'adresse publique de la question, dans le menu
            // des trois points. La VERSION et non l'identifiant public : c'est
            // ce que le serveur reçoit, et c'est aussi ce que porte une
            // question d'archive, où le même geste existe.
            onPartagerDehors={() => {
              setASortir({ version: carte.form_version_id, enonce: carte.stem_md })
            }}
            onQuiz={
              onQuiz === undefined
                ? undefined
                : () => {
                    onQuiz({ ...carte.tag, item: carte.item_id })
                  }
            }
          />
        </Sentinelle>
        </Fragment>
      ))}

      {fil.isFetchingNextPage ? <FeedSkeleton /> : null}
    </TirerPourRafraichir>
  )
}

/**
 * RG-D16 — un écran vide dit ce qui manque et quoi faire.
 *
 * « Rien à afficher » laisse croire à une panne, et les trois listes sont
 * vides pour trois raisons différentes : aucun thème suivi, rien mis de côté,
 * ou — la meilleure nouvelle des trois — plus rien à rattraper.
 */
function Vide({ mode }: { mode: ModeDuFil }) {
  const textes: Record<ModeDuFil, { titre: string; detail: string }> = {
    feed: {
      titre: 'Rien à réviser pour l’instant',
      detail:
        'Choisissez des thèmes à suivre : ce sont eux, et eux seuls, qui composent votre fil.',
    },
    bookmarks: {
      titre: 'Aucun marque-page',
      detail: 'Touchez « Marquer » sur une question pour la retrouver ici.',
    },
    review: {
      titre: 'Rien à revoir',
      detail:
        'Les questions ratées apparaissent ici jusqu’à ce que vous les réussissiez deux fois de suite.',
    },
  }

  const { titre, detail } = textes[mode]

  return (
    <div className="px-4 py-10 text-center">
      <p className="text-[20px] font-bold">{titre}</p>
      <p className="mt-2 text-[15px]" style={{ color: 'var(--texte-2)' }}>
        {detail}
      </p>
    </div>
  )
}

/**
 * Déclenche le préchargement quand la carte entre dans le champ.
 *
 * `IntersectionObserver` plutôt qu'un calcul de position au défilement : il ne
 * réveille le fil principal que lorsque quelque chose se passe, là où un
 * gestionnaire de `scroll` s'exécute à chaque pixel. Sur un Moto G4, c'est la
 * différence entre un défilement fluide et un défilement qui accroche.
 */
function Sentinelle({
  actif,
  onAtteinte,
  children,
}: {
  actif: boolean
  onAtteinte: () => void
  children: React.ReactNode
}) {
  const ancre = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const noeud = ancre.current

    if (!actif || noeud === null || typeof IntersectionObserver === 'undefined') return

    const observateur = new IntersectionObserver((entrees) => {
      if (entrees.some((entree) => entree.isIntersecting)) onAtteinte()
    })

    observateur.observe(noeud)

    return () => {
      observateur.disconnect()
    }
  }, [actif, onAtteinte])

  return <div ref={ancre}>{children}</div>
}
